// Content model for the "How It Works" animated workflow comparison page.
// Copy is qualitative on purpose (no fabricated stats). Each stage carries
// audience-specific phrasing for both the Traditional and LXRA AI-Powered
// columns; the layout never changes between audiences, only the wording.

export const STAGES = [
  {
    slug: 'booking',
    title: 'Booking',
    toolBadges: ['Marketplace', 'Availability Calendar'],
  },
  {
    slug: 'shoot-planning',
    title: 'Shoot Planning',
    toolBadges: ['AI Blueprint', 'Location Scout'],
  },
  {
    slug: 'shoot-day',
    title: 'Shoot Day',
    toolBadges: ['Pose Assistant'],
  },
  {
    slug: 'culling',
    title: 'Culling',
    toolBadges: ['AI Culling Assist'],
  },
  {
    slug: 'color-grading',
    title: 'Color Grading',
    toolBadges: ['Color Intelligence'],
  },
  {
    slug: 'delivery',
    title: 'Delivery',
    toolBadges: ['Client Gallery'],
  },
];

export const STAGE_CONTENT = {
  booking: {
    client: {
      traditional: {
        title: 'Back-and-forth by email',
        body: 'You reach out to a few photographers, wait for replies, and try to compare availability and pricing across scattered messages.',
        bullets: ['Manual email threads', 'No visibility into real availability', 'Pricing scattered across messages'],
      },
      lxra: {
        title: 'Browse, check, request',
        body: 'Browse verified photographers in the LXRA Marketplace, see real availability on a calendar, and send a request to book in minutes.',
        bullets: ['Verified photographer directory', 'Live availability calendar', 'Request-to-book in one place'],
      },
    },
    photographer: {
      traditional: {
        title: 'Inquiries handled by hand',
        body: 'You juggle inbound inquiries manually, checking a personal calendar and retyping the same package details for every lead.',
        bullets: ['Repetitive replies to every inquiry', 'Availability tracked outside the system', 'Easy to lose track of a lead'],
      },
      lxra: {
        title: 'One dashboard, real availability',
        body: 'New requests land in one dashboard with your real availability already synced, so approving a booking takes a couple of clicks.',
        bullets: ['Centralized booking requests', 'Availability calendar built in', 'Packages & deposits pre-defined'],
      },
    },
  },
  'shoot-planning': {
    client: {
      traditional: {
        title: 'Hope it translates',
        body: "You describe the vision on a call and hope the photographer catches every detail, crossing your fingers for good light on the day.",
        bullets: ['Vision relayed verbally', 'Little visibility into the plan', 'Weather and light are a guess'],
      },
      lxra: {
        title: 'See the plan before it starts',
        body: 'Your ideas become a Creative Blueprint — direction, shot list, and locations — that you can see before the shoot even begins.',
        bullets: ['Shared creative direction upfront', 'Clear shot list & mood', 'Locations matched to your story'],
      },
    },
    photographer: {
      traditional: {
        title: 'Scouting from memory',
        body: 'You research locations manually, estimate sun position from experience, and sketch a shot list the night before.',
        bullets: ['Manual location scouting', 'Sun position estimated by memory', 'Shot list built ad hoc'],
      },
      lxra: {
        title: 'A blueprint, and real light data',
        body: 'LXRA Intelligence turns a brief into a Creative Blueprint, and Location Scout maps real sun position and where to stand — you still make every creative call.',
        bullets: ['AI-assisted shot list & direction', 'Real sun position & light timeline', 'Where-to-stand guidance per location'],
      },
    },
  },
  'shoot-day': {
    client: {
      traditional: {
        title: 'Improvised in the moment',
        body: "You're told to relax and hope the poses feel natural in the moment, with no real preview of how a scene will look.",
        bullets: ['Posing improvised on the spot', 'No preview of how a moment will look', "Relies entirely on the photographer's memory"],
      },
      lxra: {
        title: 'Confident, unhurried direction',
        body: 'Each moment has a thoughtful pose plan behind it, so direction on the day feels confident and unhurried.',
        bullets: ['Pose ideas prepared ahead of time', 'Smoother, more confident direction', 'More time for genuine moments'],
      },
    },
    photographer: {
      traditional: {
        title: 'Directing from memory alone',
        body: 'You direct poses from memory and experience alone, re-deriving setups on the fly for every location and light change.',
        bullets: ['Posing directed from memory', 'Re-deriving setups per location', 'Harder to keep a consistent flow'],
      },
      lxra: {
        title: 'A reference, not a rulebook',
        body: 'Pose Assistant offers a few directional cues per moment as a reference — you still choose what fits the subject and the light.',
        bullets: ['On-demand pose suggestions per moment', 'Text cues, not rigid rules', 'You stay the final creative decision-maker'],
      },
    },
  },
  culling: {
    client: {
      traditional: {
        title: 'A long wait before proofing',
        body: 'You wait while every shot gets sorted one by one before you see anything worth reviewing.',
        bullets: ['Fully manual review', 'Slower turnaround before proofing', 'Duplicate or blurry shots easy to miss'],
      },
      lxra: {
        title: 'A faster path to your gallery',
        body: "The best frames reach your gallery faster, with obvious blur and duplicates cleared out before proofing begins.",
        bullets: ['Faster path to your gallery', 'Cleaner first pass', "Nothing hidden without your photographer's review"],
      },
    },
    photographer: {
      traditional: {
        title: 'Frame-by-frame, by hand',
        body: 'You scroll through every frame manually, flagging blur and duplicates one image at a time.',
        bullets: ['Manual frame-by-frame review', 'Duplicate detection by eye', 'Time-consuming before any editing starts'],
      },
      lxra: {
        title: 'A first pass, your final say',
        body: 'Culling Assist flags blur, duplicate groups, and posing notes automatically — you review, compare side-by-side, and make the final call.',
        bullets: ['Automatic blur & duplicate detection', 'Side-by-side compare tools', 'You approve every hide or delete'],
      },
    },
  },
  'color-grading': {
    client: {
      traditional: {
        title: 'One edit, applied broadly',
        body: 'Your photos get a general edit, applied the same way regardless of the specific scene or light in each frame.',
        bullets: ['One-size edit across a gallery', 'Consistency checked manually', 'Style applied by feel, not measurement'],
      },
      lxra: {
        title: 'A consistent, considered look',
        body: 'Your gallery gets a considered, consistent look — the mood you discussed, carried through every frame.',
        bullets: ['Consistent look across your gallery', 'Skin tones protected automatically', 'The photographer approves every look before delivery'],
      },
    },
    photographer: {
      traditional: {
        title: 'Checking drift by eye',
        body: 'You eyeball exposure and color drift image by image, hoping the whole gallery reads consistently by the end.',
        bullets: ['Manual consistency checks', 'Color drift caught by eye', 'Repetitive corrections across similar frames'],
      },
      lxra: {
        title: 'Measured, then yours to approve',
        body: 'Color Intelligence measures every frame against your creative direction and proposes moves — you accept, modify, or reject each one.',
        bullets: ['Per-frame consistency scoring', 'Skin-tone protected adjustments', 'Every edit stays yours to approve'],
      },
    },
  },
  delivery: {
    client: {
      traditional: {
        title: 'A link, then you sort it out',
        body: 'You wait for a download link or drive, then sort through everything yourself with no built-in way to favorite or share.',
        bullets: ['Delayed delivery', 'Files handed off with little structure', 'Favorites tracked manually by you'],
      },
      lxra: {
        title: 'Your own private gallery',
        body: 'You get a private gallery to browse, favorite, and download — ready whenever your photographer finishes.',
        bullets: ['Private client gallery', 'Favorite & download in one place', 'Share with family in a click'],
      },
    },
    photographer: {
      traditional: {
        title: 'Manual export & hand-off',
        body: 'You export, zip, and send files manually, then answer emails asking where the gallery is.',
        bullets: ['Manual export & file transfer', 'No built-in client experience', 'Favorites & downloads tracked by hand'],
      },
      lxra: {
        title: 'A branded gallery does the work',
        body: 'Delivery happens through a branded client gallery with built-in favorites, downloads, and sharing — no manual file handoffs.',
        bullets: ['Branded client gallery', 'Built-in favorites & downloads', 'Shareable links, no manual zips'],
      },
    },
  },
};

// The homepage teaser only shows a short 3-stage preview, not the full 6-stage
// timeline (the full experience lives on /how-it-works).
export const TEASER_STAGE_SLUGS = ['booking', 'shoot-planning', 'delivery'];

// Platform tour slides. `image` is intentionally left null until real product
// screenshots are supplied — the carousel renders a Skeleton placeholder in
// that case (per design guidelines: no stock photography allowed here).
export const PLATFORM_TOUR_SLIDES = [
  {
    slug: 'location-scout',
    title: 'Location Scout',
    caption: 'Real sun position, light windows, and where-to-stand guidance for any location.',
    image: null,
  },
  {
    slug: 'shoot-simulator',
    title: 'Shoot Simulator',
    caption: 'Moment-by-moment positioning diagrams built from the real site layout.',
    image: null,
  },
  {
    slug: 'culling-review',
    title: 'AI Culling Review',
    caption: 'Blur, duplicate, and posing flags reviewed side-by-side before anything is hidden.',
    image: null,
  },
  {
    slug: 'color-studio',
    title: 'Color Intelligence',
    caption: 'Per-frame consistency scoring against your creative direction, with skin tones protected.',
    image: null,
  },
  {
    slug: 'photographer-dashboard',
    title: 'Studio Dashboard',
    caption: 'Bookings, packages, earnings, and galleries — all in one place.',
    image: null,
  },
];

export const AUDIENCE_LABELS = {
  client: "I'm a Client",
  photographer: "I'm a Photographer",
};

// Icon pairing per stage (lucide-react component names resolved in
// TimelineStageRow.jsx / HowItWorksTeaser.jsx). Traditional = a plainer,
// more "manual" icon; LXRA = a smarter/assistive icon — reinforcing the
// contrast visually, not just in copy.
export const STAGE_ICON_NAMES = {
  booking: { traditional: 'MailQuestion', lxra: 'CalendarCheck' },
  'shoot-planning': { traditional: 'NotebookPen', lxra: 'Compass' },
  'shoot-day': { traditional: 'Users', lxra: 'Wand2' },
  culling: { traditional: 'ImageOff', lxra: 'ScanSearch' },
  'color-grading': { traditional: 'Palette', lxra: 'SlidersHorizontal' },
  delivery: { traditional: 'Mail', lxra: 'Gift' },
};
